import React, { useState, useEffect } from 'react';
import { eventsApi, Event, Timezone } from '../api/client';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Tooltip from './Tooltip';
import { getErrorMessage } from '../utils/errorHandler';

interface CreateEventModalProps {
  onClose: () => void;
  onCreate: (event: Event) => void;
}

const CreateEventModal: React.FC<CreateEventModalProps> = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    venue: '',
    date_time: '',
    timezone: 'Europe/London',
    max_attendees: 50,
    reserved_tickets: 0,
    admission_policy: 'review' as 'review' | 'auto',
    waitlist_when_full: true,
    event_type: 'conference' as 'social' | 'meetup' | 'conference' | 'casual',
    google_maps_link: '',
    entry_requirement: '',
    badge_subtitle: '',
    badge_title: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timezones, setTimezones] = useState<Timezone[]>([]);

  useEffect(() => {
    // Load timezones when component mounts
    eventsApi.getTimezones().then(setTimezones).catch(console.error);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // The datetime input gives us a local datetime string
      // We'll send it as-is since the backend expects local time in the event's timezone
      const newEvent = await eventsApi.createEvent({
        ...formData,
        date_time: formData.date_time + ':00', // Add seconds
        entry_requirement: formData.entry_requirement.trim() || undefined,
        badge_subtitle: formData.badge_subtitle.trim() || undefined,
        badge_title: formData.badge_title.trim() || undefined,
      });
      onCreate(newEvent);
    } catch (error) {
      setError(getErrorMessage(error));
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center sm:p-4 z-50">
      <div className="bg-white rounded-t-lg sm:rounded-lg w-full sm:max-w-md h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Create New Event</h2>
            <Tooltip text="Close">
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </Tooltip>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Title
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Summer Music Festival"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Join us for an evening of live music..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date & Time
              </label>
              <input
                type="datetime-local"
                required
                value={formData.date_time}
                onChange={(e) => setFormData({ ...formData, date_time: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Timezone
              </label>
              <select
                required
                value={formData.timezone}
                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {timezones.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Venue
              </label>
              <input
                type="text"
                required
                value={formData.venue}
                onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="City Park Amphitheater"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Google Maps Link (optional)
              </label>
              <input
                type="url"
                value={formData.google_maps_link || ''}
                onChange={(e) => setFormData({ ...formData, google_maps_link: e.target.value })}
                placeholder="https://maps.google.com/..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Badge footer (optional)
              </label>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  maxLength={100}
                  value={formData.badge_subtitle || ''}
                  onChange={(e) => setFormData({ ...formData, badge_subtitle: e.target.value })}
                  placeholder="Left, e.g. 🚀 Star IX"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  maxLength={100}
                  value={formData.badge_title || ''}
                  onChange={(e) => setFormData({ ...formData, badge_title: e.target.value })}
                  placeholder="Right, defaults to the event title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Printed in the blue band at the bottom of name badges. Emoji are fine.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Condition of entry (optional)
              </label>
              <textarea
                value={formData.entry_requirement || ''}
                onChange={(e) => setFormData({ ...formData, entry_requirement: e.target.value })}
                rows={3}
                placeholder="e.g. Valid, in-date physical photo ID is required at reception. Copies and photos of ID will not be accepted."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-sm text-gray-500">
                Shown highlighted on the sign-up form. Attendees must tick a box to confirm they will comply.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Type
              </label>
              <select
                required
                value={formData.event_type}
                onChange={(e) => setFormData({ ...formData, event_type: e.target.value as 'social' | 'meetup' | 'conference' | 'casual' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="social">Social</option>
                <option value="meetup">Meetup</option>
                <option value="conference">Conference</option>
                <option value="casual">Casual</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maximum Attendees
              </label>
              <input
                type="number"
                required
                min="1"
                value={formData.max_attendees}
                onChange={(e) => setFormData({ ...formData, max_attendees: parseInt(e.target.value) || 1 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reserved Tickets
                <span className="text-gray-500 text-xs ml-1">(Held back from public self-registration)</span>
              </label>
              <input
                type="number"
                min="0"
                value={formData.reserved_tickets || 0}
                onChange={(e) => setFormData({ ...formData, reserved_tickets: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Admission
              </label>
              <select
                value={formData.admission_policy}
                onChange={(e) => setFormData({ ...formData, admission_policy: e.target.value as 'review' | 'auto' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="review">Curated — you approve every request</option>
                <option value="auto">Self-service — instant tickets while spots remain</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {formData.admission_policy === 'review'
                  ? 'The public page offers "Request an invitation" and never says sold out.'
                  : 'Tickets are issued automatically until self-service spots run out.'}
              </p>
            </div>

            {formData.admission_policy === 'auto' && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="create_waitlist_when_full"
                  checked={formData.waitlist_when_full}
                  onChange={(e) => setFormData({ ...formData, waitlist_when_full: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="create_waitlist_when_full" className="ml-2 block text-sm text-gray-700">
                  Offer a waitlist when full
                </label>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
              >
                {loading ? 'Creating...' : 'Create Event'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateEventModal;